import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '../../../i18n/language-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { ChevronDown, Globe } from 'lucide-react';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { updateCurrentLanguage } from './services/language-service';
import { useConfig } from 'hooks/use-config';
import { cn } from 'lib/utils';

type LanguageSelectorProps = {
  variant?: 'default' | 'klax';
};

function LanguageSelector({ variant = 'default' }: LanguageSelectorProps) {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const user = useCommonStore((state) => state.user);
  const { currentLanguage, setLanguage, availableLanguages, isLoading } = useLanguageContext();
  const { configurationSettings } = useConfig();

  const changeLanguage = async (newLanguageCode: string) => {
    if (user) {
      updateCurrentLanguage(user.id, newLanguageCode, configurationSettings);
    }
    await setLanguage(newLanguageCode);
  };

  const languageCode = currentLanguage.split('-')[0].toUpperCase();
  const isKlax = variant === 'klax';

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <button
          type="button"
          className={cn(
            isKlax
              ? 'flex items-center justify-center gap-1 px-3 py-2'
              : 'flex h-9 w-9 items-center gap-2 rounded-full border border-border px-1.5 text-foreground sm:h-9 sm:w-auto sm:px-3'
          )}
        >
          {!isKlax && <Globe size={20} className="hidden text-sm sm:block" />}
          <span
            className={cn(
              isKlax
                ? 'text-sm font-normal leading-[1.2] text-black dark:text-true-white'
                : 'text-sm font-medium sm:block'
            )}
          >
            {languageCode}
          </span>
          {isKlax && (
            <ChevronDown
              className="size-4 shrink-0 text-[#3d3d3d] text-black dark:text-true-white"
              aria-hidden
            />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {isLoading ? (
          <DropdownMenuItem disabled>{t('LOADING_LANGUAGES')}...</DropdownMenuItem>
        ) : !availableLanguages || availableLanguages.length === 0 ? (
          <DropdownMenuItem disabled>{t('PLEASE_LOGIN_FIRST_ACCESS_LANGUAGES')}</DropdownMenuItem>
        ) : availableLanguages && availableLanguages.length > 0 ? (
          availableLanguages.map((lang, i) => (
            <div key={lang.itemId}>
              <DropdownMenuItem
                className={`${lang.languageCode === currentLanguage ? 'font-bold cursor-pointer' : ''}`}
                onClick={() => changeLanguage(lang.languageCode)}
              >
                {lang.languageName}
              </DropdownMenuItem>
              {i !== availableLanguages.length - 1 && <DropdownMenuSeparator />}
            </div>
          ))
        ) : (
          <DropdownMenuItem disabled>{t('NO_LANGUAGES_AVAILABLE')}</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
